import { Injectable } from '@angular/core';

import { MessageService } from 'primeng/api';

import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { StatisticsReportService } from '../../../../Common/TargetingShared/services/statistics.report.service';
import { BriefingsApi } from '../../../../api/briefings/briefings.api';
import { BriefingsService } from '../../services/briefings.service';
import { StatisticsApiInterface } from '../../../../Common/TargetingShared/model/statisticsReport/statistics-api.model';
import { CurrentResourceInterface } from '../../../../Common/Basic/models/current-resource-abstract.model';
import { catchError, combineLatest, filter, map, mergeMap, of } from 'rxjs';
import { LinksReportModel } from '../../../../Common/TargetingShared/components/statisticReport/PerformanceReport/links-report.model';
import { LinkStatsApiModel } from '../../../../Common/TargetingShared/model/statisticsReport/link-stats-api.model';
import { BriefingStatusEnum } from '../../../../Common/MonitorShared/enum/briefing-status.enum';
import { MonitoringRoutingService } from '../../../Core/services/monitoring-routing.service';
import { RouteType } from '../../../Shared/enums/route-type.enum';
import { HttpErrorResponse } from '@angular/common/http';
import { BriefingHistorySentModel } from '../../../../api/briefings/model/briefing-history-sent.model copy';

@Injectable()
export class BriefingReportService extends StatisticsReportService {

    linksReport$ = combineLatest([this.mppEstimatesEnabledSubject, this.resourceIdSubject, this.recipientReportData$])
        .pipe(map(([isMppEstimatesEnabled, id, recipientReportData]) => {
            if (!id) {
                return null;
            }

            const linkStats: LinkStatsApiModel[] = [];

            if (recipientReportData?.linkTexts?.length) {
                recipientReportData.linkTexts.forEach(linkText => {
                    linkStats.push({
                        url: linkText.clickedUrl,
                        clickCount: linkText.totalClicks,
                        uniqueClickCount: linkText.uniqueClicks
                    });
                });
            }

            const res = new LinksReportModel(linkStats);
            this.calculateLinkReports(res, isMppEstimatesEnabled, recipientReportData);

            return res;
        }));

    forceRefresh: boolean = false;
    subId: number;

    sentHistory: BriefingHistorySentModel;

    constructor(
        protected briefingsApi: BriefingsApi,
        protected briefingsService: BriefingsService,
        protected messageService: MessageService,
        protected localizationService: LocalizationService,
        private routingService: MonitoringRoutingService,
    ) {
        super(
            messageService,
            localizationService
        );

        this.setupSubscription();
    }

    getResourceApi(): StatisticsApiInterface {
        return this.briefingsApi;
    }

    getResourceService(): CurrentResourceInterface {
        return this.briefingsService;
    }

    override setupSubscription(): void {
        this.subscriptions.push(
            this.briefingsService.getForceReportRefresh().pipe(filter(x => !!x)).subscribe(forceRefresh => {
                this.forceRefresh = forceRefresh;
            }),
            this.briefingsService.getFocusedResource().pipe(filter(x => !!x)).pipe(mergeMap((resource) => {
                if (!this.forceRefresh && (this.cachedResourceId === resource.id)) {
                    return of(null);
                }
                this.forceRefresh = false;
                this.cachedResourceId = resource.id;

                if (resource.status === BriefingStatusEnum.SENT ||
                    resource.status === BriefingStatusEnum.SCHEDULED ||
                    resource.status === BriefingStatusEnum.ACTIVE
                ) {
                    return this.briefingsApi.getSentResources(resource.id).pipe(catchError(
                        (err: HttpErrorResponse) => {
                            if (err.status === 404) {
                                // B/E contains bad data
                                return of(new BriefingHistorySentModel());
                            }
                        }
                    ));
                } else {
                    return of(null);
                }
            })).subscribe(sentHistory => {
                if (!sentHistory) {
                    return;
                }
                this.sentHistory = sentHistory;

                if (!this.setResourceUid()) {
                    if (sentHistory.sentDates?.length > 0) {
                        const id = sentHistory.sentDates[sentHistory.sentDates.length - 1].briefingSentId;
                        this.setResourceId(id);
                        this.resourceUuidSubject.next(sentHistory.sentDates[sentHistory.sentDates.length - 1].guid);
                    } else {
                        this.setResourceId(null);
                        this.resourceUuidSubject.next(null);
                    }
                }
            }),
            this.routingService.getCurrentRoute().subscribe(route => {
                this.subId = null;
                if (((route.type === RouteType.recurringBriefingReport) || (route.type === RouteType.recurringCanvasReport)) && route.subId) {
                    this.subId = +route.subId;
                    this.setResourceId(+route.subId);
                    if (this.sentHistory) {
                        this.setResourceUid();
                    }
                }
            })
        );
    }

    // used for canvas briefings, the logic old (above) would only give you the latest UUID, not on the one selected (match the briefing history ID)
    setResourceUid(): boolean {
        let briefingMatched = false;
        if (this.subId && this.sentHistory?.sentDates?.length && this.sentHistory?.sentDates[0].hasOwnProperty('briefingSentId')) {
            for (const dates of this.sentHistory.sentDates) {
                if (dates.briefingSentId === this.subId) {
                    this.setResourceId(this.subId);
                    this.resourceUuidSubject.next(dates.guid);
                    briefingMatched = true;
                    return briefingMatched;
                }
            }
        }

        return briefingMatched;
    }
}
